import { DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AbilityService } from '@casl/angular';
import { SessionService } from 'src/app/services/session-service/session.service';
import {
  buscarIngressosPorEvento,
  buscarMetaDeIngressos,
} from 'src/app/api/generated/v3/ingresso/ingresso';
import type { BuscarIngressosPorEventoParams } from 'src/app/api/generated/v3/schemas/buscarIngressosPorEventoParams';
import type { IngressosCheckInDto } from 'src/app/api/generated/v3/schemas/ingressosCheckInDto';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  from,
  map,
  of,
} from 'rxjs';
import Swal from 'sweetalert2';
import {
  EVENTOS_PUBLIC_ID,
  INGRESSOS_REENVIAR,
  SESSOES_POR_EVENTO,
} from 'src/app/core/config/endpoints';
import { closeLoader, dispararLoader } from 'src/app/helpers/swall.helpers';
import { replaceEdpointParams } from 'src/app/helpers/endpoints-helpers';
import {
  adminUrl,
  AdminPaginatorComponent,
  AdminTableComponent,
  createPagedList,
  parsePagedResponse,
  type PageChange,
  type RowAction,
} from '../../admin/shared';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCamera,
  lucideChevronLeft,
  lucideClock,
  lucideCircleCheckBig,
  lucideCircleDashed,
  lucidePrinter,
} from '@ng-icons/lucide';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmButton } from '@spartan-ng/helm/button';
import { ModalConvitesEventosDadosComponent } from '../sub-components/modal-convites-eventos-dados/modal-convites-eventos-dados.component';
import { EventoAbility } from '../eventos.ability';
import { CheckinEtiquetaComponent } from './checkin-etiqueta.component';
import { CheckinAbilityHandoff } from './checkin-ability-handoff';
import { construirAcoes } from './checkin-acoes';
import { corpoConfirmarCheckin, corpoJaRealizado } from './checkin-dialogos';
import { CheckinDetalhesComponent } from './checkin-detalhes.component';
import { CheckinRetroativoComponent } from './checkin-retroativo.component';
import { ESTADO_PADRAO, estadoParaParams, paramsParaEstado } from './checkin-deeplink';
import {
  achatarFiltros,
  camposDeBusca,
  chavesDeFiltro,
  filtrosParaChips,
  OPERADOR_TEXTO,
  ORDENACOES,
  periodoParaFiltro,
  sessoesParaOpcoes,
  type FiltrosDinamicos,
} from './checkin-filtros';
import {
  dadosJaRealizadoParcial,
  dadosIngressoExpirado,
  detalheProblema,
  ehCheckinJaRealizado,
  ehIndisponivel,
  ehIngressoExpirado,
  ehSemPermissao,
  comCodigoDeSuporte,
  ehSessaoExpirada,
  opcoesErro,
} from './checkin-erros';
import { CheckinV3Service } from './checkin-v3';
import {
  TEXTO_CONSENTIMENTO_EXPORTACAO,
  baixarCsv,
  linhasParaCsv,
  nomeArquivoCsv,
  paramsExportacao,
} from './checkin-export';
import {
  lerHistorico,
  registrar,
  type CheckinRegistrado,
} from './checkin-historico';
import { CheckinImpressoraComponent } from './checkin-impressora.component';
import { CheckinScannerComponent } from './checkin-scanner.component';
import { CheckinToolbarComponent } from './checkin-toolbar.component';
import {
  CHECKIN_BADGE_COLUMNS,
  CHECKIN_COLUMNS,
  CHECKIN_COLUNAS_OCULTAS,
  CHECKIN_COLUNAS_TRUNCADAS,
} from './checkin.columns';
import {
  buildCheckinRowVm,
  type CheckinRowVm,
  type CheckinVmContext,
  type JanelaVm,
} from './checkin-row.vm';
import { EventoSidebarComponent } from './evento-sidebar.component';

interface Toast {
  titulo: string;
  detalhe: string;
  podeDesfazer: boolean;
}

@Component({
  selector: 'app-checkin-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    HlmBadge,
    HlmButton,
    AdminTableComponent,
    AdminPaginatorComponent,
    EventoSidebarComponent,
    CheckinDetalhesComponent,
    CheckinRetroativoComponent,
    CheckinImpressoraComponent,
    CheckinToolbarComponent,
    CheckinScannerComponent,
    ModalConvitesEventosDadosComponent,
    CheckinEtiquetaComponent,
    NgIcon,
    RouterLink,
    ...HlmDropdownMenuImports,
  ],
  providers: [
    provideIcons({
      lucideCamera,
      lucideChevronLeft,
      lucideClock,
      lucideCircleCheckBig,
      lucideCircleDashed,
      lucidePrinter,
    }),
    CheckinV3Service,
  ],
  templateUrl: './checkin-page.component.html',
})
export class CheckinPageComponent implements OnInit, OnDestroy {
  // `inject()` em campo de classe respeita ordem de declaração (ES2022):
  // dependências antes de quem as usa.
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpClient);
  /** Escrita de check-in pela v3 — ver `checkin-v3.ts`. */
  private readonly v3 = inject(CheckinV3Service);
  private readonly abilityService = inject<AbilityService<EventoAbility>>(AbilityService);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly abilityHandoff = inject(CheckinAbilityHandoff);
  private readonly eventoAbility = inject(EventoAbility);

  readonly idEvento = this.route.snapshot.paramMap.get('idEvento') ?? '';

  /**
   * `equal: () => false` não é micro-otimização às avessas: o `ability$` do
   * CASL emite SEMPRE a mesma instância de Ability (ela é mutável), então com a
   * igualdade padrão do signal (`Object.is`) um `update()` não conta como
   * mudança e nada recalcula — a sidebar continuaria vazia mesmo depois de
   * republicar as regras.
   */
  readonly ability = toSignal(this.abilityService.ability$, {
    initialValue: null,
    equal: () => false,
  });

  readonly evento = toSignal(
    this.http
      .get<Record<string, unknown>>(adminUrl(replaceEdpointParams(EVENTOS_PUBLIC_ID, this.idEvento)))
      .pipe(
        map((r) => (r?.['data'] as Record<string, unknown>) ?? r),
        catchError(() => of(null)),
      ),
    { initialValue: null },
  );

  readonly checkinsRealizados = signal(0);
  readonly checkinsRestantes = signal(0);

  /**
   * Filtros pré-definidos, semeados pela URL. Só entram os que o backend
   * aceita — ver checkin-filtros.ts.
   */
  private readonly estadoInicial = paramsParaEstado(this.route.snapshot.queryParamMap);
  readonly sessaoId = signal<string | null>(this.estadoInicial.sessaoId);
  readonly ordem = signal<string>(this.estadoInicial.ordem);

  /**
   * GUE-391 (ADR-0019): filtros dinâmicos aplicados agora, chaveados por
   * `path` do catálogo — nenhum nome de filtro (`loteId`, `status`, ...) é
   * hardcoded neste componente. Ver {@link FiltrosDinamicos}.
   */
  readonly filtros = signal<FiltrosDinamicos>(this.estadoInicial.filtros);

  /**
   * Catálogo de `/filtros`, traduzido em chips pelo `checkin-filtros.ts`. Sem
   * hardcode: se o backend não descrever `loteId`, o chip correspondente
   * simplesmente não existe na toolbar. Erro (ex. sem permissão) vira
   * catálogo vazio, não tela quebrada — mesmo padrão de `sessoes` abaixo.
   */
  private readonly meta = toSignal(
    from(buscarMetaDeIngressos(this.http, String(this.idEvento))).pipe(
      catchError(() => of({ filtros: [], ordenacoes: [] })),
    ),
    { initialValue: { filtros: [], ordenacoes: [] } },
  );
  private readonly filtrosCatalogo = computed(() => this.meta().filtros ?? []);
  /**
   * Ordenações do servidor. Catálogo vazio (erro, ou rota que ainda não publica)
   * mantém a lista local: lista vazia não é "esta tela não ordena", e apagar o
   * chip por isso tiraria um controle que o servidor honra.
   */
  readonly ordenacoes = computed(() => {
    const doServidor = this.meta().ordenacoes ?? [];
    return doServidor.length > 0
      ? doServidor.map((o) => ({ valor: o.value, rotulo: o.label }))
      : ORDENACOES;
  });
  readonly chipsFiltro = computed(() => filtrosParaChips(this.filtrosCatalogo()));
  /**
   * O mesmo catálogo na forma da barra de busca por prefixo: inclui os filtros
   * textuais (que não viram chip) e carrega o prefixo digitável de cada campo.
   */
  readonly camposBusca = computed(() => camposDeBusca(this.filtrosCatalogo()));
  /** Chaves `campo[operador]` que o catálogo atual pode ter — usado só pra zerar as que saem de uso. */
  private readonly chavesFiltroConhecidas = computed(() => chavesDeFiltro(this.camposBusca()));

  /**
   * `api/v1/sessoes/evento/:id` existe e é guardada por gerente/sócio/criador/
   * admin. Quem não tem o papel recebe 403 — daí o catchError devolver lista
   * vazia em vez de quebrar: sem sessões, o chip simplesmente não aparece.
   */
  readonly sessoes = toSignal(
    this.http
      .get<unknown>(adminUrl(replaceEdpointParams(SESSOES_POR_EVENTO, { idEvento: this.idEvento })))
      .pipe(
        map((r) => sessoesParaOpcoes((r as { data?: unknown })?.data ?? r)),
        catchError(() => of([])),
      ),
    { initialValue: [] },
  );

  readonly colunas = CHECKIN_COLUMNS;
  readonly colunasBadge = CHECKIN_BADGE_COLUMNS;
  readonly colunasTruncadas = CHECKIN_COLUNAS_TRUNCADAS;
  readonly colunasOcultas = CHECKIN_COLUNAS_OCULTAS;

  /**
   * Lista pelo client v3 gerado (`GET /api/v3/eventos/:id/ingressos`), no lugar
   * do `api/v2/ingresso/:id/buscar`. Mesmos nomes de campo dos dois lados — o
   * viewmodel não muda —, e a v3 devolve `{ content, page }`, que o
   * `parsePagedResponse` já entende.
   */
  readonly list = createPagedList<IngressosCheckInDto>({
    initialPageSize: 25,
    initialSearch: this.estadoInicial.filtro,
    initialPageIndex: this.estadoInicial.pagina,
    // `sort` e `sessaoId` vão como params dinâmicos (forma-função): são lidos a
    // cada fetch, então trocar o chip e chamar setPage(0) já refaz a busca.
    // GUE-391: `achatarFiltros` entra do mesmo jeito — `campo[operador]` já
    // achatado (o `PagedListQuery` é `Record<string,string>`, sem objeto
    // aninhado). Nenhum filtro é nomeado aqui: o que existe em `this.filtros()`
    // é inteiramente o que o catálogo (`/filtros`) e a interação do usuário
    // determinaram.
    params: () => ({
      sort: this.ordem(),
      sessaoId: this.sessaoId(),
      ...achatarFiltros(this.filtros()),
    }),
    // O client gerado NÃO tem um param tipado por filtro (de propósito — ver
    // ADR-0019: o contrato não nomeia filtro nenhum). As chaves `campo[operador]`
    // entram no MESMO objeto de params dos tipados — o gerado serializa tudo que
    // chega ali —, com um cast porque o contrato não as declara.
    fetcher: (q, signal) => {
      const { filtro, page, size, sort, sessaoId, ...filtrosDinamicos } = q;
      // Os tipados ficam num objeto anotado: o cast é obrigatório na fusão (o spread de
      // Record<string,string> introduz index signature e conflita com `page: number`), mas
      // castar o literal inteiro apagaria a checagem justamente dos campos do contrato — um
      // param renomeado na v3 passaria batido e a busca pararia de filtrar em silêncio.
      const tipados: BuscarIngressosPorEventoParams = {
        filtro,
        page: Number(page),
        size: Number(size),
        sort,
        sessaoId,
      };
      return buscarIngressosPorEvento(
        this.http,
        String(this.idEvento),
        { ...tipados, ...filtrosDinamicos } as BuscarIngressosPorEventoParams,
        { signal },
      );
    },
  });

  /**
   * O `agora` entra como signal para as janelas reavaliarem sozinhas na virada
   * do minuto — sem isso uma janela que abriu às 18h continuaria "Ainda não
   * abriu" até o próximo fetch.
   */
  private readonly agora = signal(new Date());
  private readonly relogio = setInterval(() => this.agora.set(new Date()), 30_000);

  readonly vmContext = computed<CheckinVmContext>(() => {
    const ability = this.ability();
    return {
      agora: this.agora(),
      podeCertificado: !!ability?.can('read', 'Certificados'),
      // `read` em Check-ins, e NÃO em ControleEntradaSaida: o papel CHECKIN e o
      // escopo `evento:checkin:perform` recebem create/update naquele subject e
      // NUNCA read (ver eventos.ability.ts), então o teste antigo escondia a
      // impressão de etiqueta justamente de quem segura a impressora térmica.
      // Este é o mesmo critério que a ação "Imprimir QR Code" já usava para
      // abrir ESTE mesmo modal — ela era o duplicado que mascarava o problema.
      podeImprimirEtiqueta: !!ability?.can('read', 'Check-ins'),
      podeDesfazer: false, // sem endpoint de reversão; ver checkin-row.vm.ts
      visiveis: {
        checkin: true,
        desfazer: true,
        reenviar: true,
        certificado: true,
        imprimirEtiqueta: true,
        // ponytail: credenciamento (associar código / vincular face) não foi
        // portado — continua na aba antiga em vez de virar botão morto aqui.
        credenciamento: false,
        camposExtras: true,
      },
    };
  });

  readonly linhas = computed<CheckinRowVm[]>(() =>
    // O VM lê a linha como saco de campos (`Record<string, unknown>`) porque
    // aceita as três grafias de check-in que o backend já usou. O DTO gerado é
    // uma interface, que o TS não atribui a um index signature — daí o cast,
    // no único ponto de fronteira.
    this.list
      .rows()
      .map((raw) => buildCheckinRowVm(raw as unknown as Record<string, unknown>, this.vmContext())),
  );

  /** Nome do operador logado, para carimbar no histórico local. */
  readonly operador = computed(() =>
    [this.session.user?.['nome'], this.session.user?.['sobrenome']].filter(Boolean).join(' ') ||
    String(this.session.user?.['email'] ?? 'Operador'),
  );

  /** Check-ins que ESTE operador fez agora — ver checkin-historico.ts. */
  readonly historico = signal<CheckinRegistrado[]>(lerHistorico(this.idEvento));
  readonly ultimoCheckin = computed<CheckinRegistrado | null>(() => this.historico()[0] ?? null);

  readonly presentes = computed(() => this.checkinsRealizados());
  /**
   * Total do EVENTO, somado do próprio balanço (realizados + restantes). Não
   * usa `list.totalRows()`: aquele é o total do FILTRO atual, e cruzar os dois
   * produzia coisas como "27 de 1 presentes" com uma busca ativa. O painel fala
   * do evento; a paginação fala do filtro.
   */
  readonly totalEvento = computed(() => this.checkinsRealizados() + this.checkinsRestantes());
  readonly pendentes = computed(() => this.checkinsRestantes());
  readonly progressoPct = computed(() => {
    const total = this.totalEvento();
    return total > 0 ? Math.round((this.presentes() / total) * 100) : 0;
  });

  readonly acoes: RowAction<CheckinRowVm>[] = construirAcoes({
    checkin: (row) => this.confirmarCheckin(row),
    camposExtras: (row) => this.detalhes().abrir(row),
    reenviar: (row) => this.reenviarIngresso(row),
    imprimirEtiqueta: (row) => this.etiqueta().abrir(row.bruto),
  });

  /**
   * Check-in retroativo de UMA janela (GUE-430): o painel de detalhes diz qual,
   * o modal coleta o horário e registra.
   */
  abrirRetroativo({ row, janela }: { row: CheckinRowVm; janela: JanelaVm }): void {
    this.retroativo().abrir(this.idEvento, row, janela);
  }

  /**
   * O modal já registrou (ou descobriu, pelo 409, que alguém registrou antes):
   * a lista e o balanço na tela estão velhos.
   */
  aoRegistrarRetroativo(): void {
    this.list.refresh();
    this.buscarBalanco();
  }

  /** Linhas com um check-in em voo — a linha some do caminho do dedo nervoso. */
  readonly emCheckin = signal<ReadonlySet<string>>(new Set());

  /** Scanner de QR: fullscreen no mobile, prévia no trilho do desktop. */
  readonly scannerAberto = signal(false);
  /** Houve check-in pela câmera desde a última recarga? Ver `fecharScanner`. */
  private listaDesatualizada = false;
  readonly exportando = signal(false);

  /**
   * Quem pode levar embora a lista com dados pessoais: gerente, sócio/dono do
   * evento e admin — não o operador de portaria.
   *
   * `read Vendas` é o recorte que separa exatamente esses perfis: o papel
   * CHECKIN recebe SÓ `Check-ins` e `ControleEntradaSaida` (ver
   * eventos.ability.ts), enquanto GERENTE e PRODUTOR recebem o resto. Não há
   * assunto próprio de "exportar" no modelo, e `Vendas` é o que já governa a
   * exportação equivalente na tela de vendas — de onde, aliás, veio palavra
   * por palavra o texto de consentimento LGPD deste diálogo
   * (TEXTO_CONSENTIMENTO_EXPORTACAO, em checkin-export.ts).
   */
  readonly podeExportar = computed(() => !!this.ability()?.can('read', 'Vendas'));
  readonly mobile = signal(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches,
  );

  readonly selecionados = signal<(string | number)[]>([]);
  readonly emLote = signal(false);

  readonly toast = signal<Toast | null>(null);
  private toastTimer?: ReturnType<typeof setTimeout>;

  readonly detalhes = viewChild.required(CheckinDetalhesComponent);
  readonly retroativo = viewChild.required(CheckinRetroativoComponent);
  readonly impressora = viewChild(CheckinImpressoraComponent);
  readonly convites = viewChild.required(ModalConvitesEventosDadosComponent);
  readonly etiqueta = viewChild.required(CheckinEtiquetaComponent);

  readonly sentinela = viewChild<ElementRef<HTMLElement>>('sentinela');
  private observer?: IntersectionObserver;

  readonly rowId = (r: CheckinRowVm) => r.id;
  /** Rótulo do checkbox: "Selecionar João da Silva", não "caixa de seleção". */
  readonly rowLabel = (r: CheckinRowVm) => r.nome;

  constructor() {
    this.buscarBalanco();

    this.digitando
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((termo) => this.list.setSearch(termo));

    // Espelha o estado na URL para o filtro ser compartilhável. `replaceUrl`
    // de propósito: sem ele, cada tecla da busca vira uma entrada de histórico
    // e o Voltar do navegador desfaz letra por letra em vez de sair da tela.
    effect(() => {
      const queryParams = estadoParaParams(
        {
          filtro: this.list.search(),
          sessaoId: this.sessaoId(),
          ordem: this.ordem(),
          pagina: this.list.pageIndex(),
          filtros: this.filtros(),
        },
        this.chavesFiltroConhecidas(),
      );
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

    // Scroll infinito do mobile. O observer só existe quando a sentinela está
    // no DOM (ou seja, no breakpoint mobile e com página seguinte disponível).
    effect((onCleanup) => {
      const alvo = this.sentinela()?.nativeElement;
      if (!alvo) return;
      const obs = new IntersectionObserver((entradas) => {
        if (entradas.some((e) => e.isIntersecting)) this.list.loadMore();
      });
      obs.observe(alvo);
      this.observer = obs;
      onCleanup(() => obs.disconnect());
    });
  }

  /**
   * Republica a ability que o guard resolveu. Tem de ser aqui, e não no guard:
   * a rota anterior é destruída DEPOIS dos guards, e o destroy do
   * `eventos-insert-edit` faz `ability.update([])`. Ver CheckinAbilityHandoff.
   */
  ngOnInit(): void {
    const regras = this.abilityHandoff.consumir();
    if (regras.length) this.eventoAbility.update(regras);
  }

  ngOnDestroy(): void {
    clearInterval(this.relogio);
    clearTimeout(this.toastTimer);
    this.observer?.disconnect();
  }

  /**
   * Valor do campo, imediato. A busca em si é debounced — sem isto cada tecla
   * virava uma requisição, e o cancelamento da anterior (paged-list) só
   * escondia o desperdício.
   */
  readonly buscaLocal = signal(this.estadoInicial.filtro);
  private readonly digitando = new Subject<string>();

  buscar(termo: string): void {
    this.buscaLocal.set(termo);
    this.digitando.next(termo);
  }

  filtrarSessao(id: string | null): void {
    this.sessaoId.set(id);
    this.list.setPage(0);
  }

  ordenar(valor: string): void {
    this.ordem.set(valor);
    this.list.setPage(0);
  }

  limparFiltros(): void {
    this.sessaoId.set(null);
    this.ordem.set(ESTADO_PADRAO.ordem);
    this.filtros.set({});
    this.list.setPage(0);
  }

  /** GUE-391: `path` vem do catálogo — sem hardcode de nome de filtro aqui. */
  filtrarLista({ path, valor }: { path: string; valor: string | null }): void {
    this.aplicarFiltro(path, 'eq', valor);
  }

  /** Filtro textual vindo da barra de busca (`nome:maria`). */
  filtrarTexto({ path, valor }: { path: string; valor: string | null }): void {
    this.aplicarFiltro(path, OPERADOR_TEXTO, valor);
  }

  private aplicarFiltro(path: string, operador: string, valor: string | null): void {
    this.filtros.update((atual) => {
      const proximo = { ...atual };
      if (valor) proximo[path] = { [operador]: valor };
      else delete proximo[path];
      return proximo;
    });
    this.list.setPage(0);
  }

  /**
   * Chip (mesma data nos dois campos) e barra (`data:26/08/2026`) chegam os
   * dois por aqui — quem decide entre `eq` e o par gte/lte é
   * {@link periodoParaFiltro}, num lugar só.
   */
  filtrarPeriodo({ path, gte, lte }: { path: string; gte: string | null; lte: string | null }): void {
    this.filtros.update((atual) => {
      const proximo = { ...atual };
      const porOperador = periodoParaFiltro(gte, lte);
      if (Object.keys(porOperador).length) proximo[path] = porOperador;
      else delete proximo[path];
      return proximo;
    });
    this.list.setPage(0);
  }

  onPageChange(ev: PageChange): void {
    ev.pageSize !== this.list.pageSize()
      ? this.list.setPageSize(ev.pageSize)
      : this.list.setPage(ev.pageIndex);
  }

  buscarBalanco(): void {
    this.http
      .get<unknown>(adminUrl(`api/v1/ingresso/${this.idEvento}/balanco`), {
        params: { isProduto: 'false' },
      })
      .pipe(catchError(() => of(null)))
      .subscribe((resp) => {
        const raw = resp as { data?: unknown } | null;
        const data = (raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw) as
          | Record<string, unknown>[]
          | Record<string, unknown>
          | null;
        // SOMA todos os lotes. O balanço é `group by l.id` — uma linha POR
        // LOTE —, então ler só `data[0]` conta o primeiro lote e ignora o
        // resto: num evento com mais de um lote o painel dizia "0 presentes"
        // com a linha ao lado mostrando check-in feito. Bug herdado da tela
        // antiga, que fazia exatamente isso.
        const lotes = Array.isArray(data) ? data : data ? [data] : [];
        const soma = (campo: 'checkInsRealizados' | 'checkInsRestantes') =>
          lotes.reduce((acc, l) => acc + (Number((l as Record<string, unknown>)?.[campo]) || 0), 0);
        this.checkinsRealizados.set(soma('checkInsRealizados'));
        this.checkinsRestantes.set(soma('checkInsRestantes'));
      });
  }

  /**
   * Confirmação identificando a pessoa pelo NOME (o diálogo antigo mostrava só
   * o e-mail) e avisando quando o check-in vai cair fora da janela — o VM já
   * sabe disso pelo `forcaraCheckin`, então a tela não precisa gastar uma ida
   * ao servidor para descobrir.
   */
  confirmarCheckin(row: CheckinRowVm): void {
    const fora = row.forcaraCheckin;
    Swal.fire({
      icon: fora ? 'warning' : 'question',
      ...corpoConfirmarCheckin(row),
    }).then((r) => {
      if (r.isConfirmed) this.registrarCheckin(row, fora);
    });
  }

  /**
   * 409 CHECKIN_JA_REALIZADO. Mostra QUEM já entrou, QUANDO e por QUEM — é o
   * que resolve a discussão na porta. Sem isto o operador via só "código não
   * encontrado ou já utilizado", que joga fora tudo que o backend mandou.
   *
   * Devolve a promessa do diálogo: a câmera só retoma depois do OK, como no
   * fluxo de check-in do app.
   */
  private avisarJaRealizado(err: unknown, linha?: CheckinRowVm): Promise<unknown> {
    // Janelas só existem na linha carregada — o corpo do 409 não as traz.
    return Swal.fire(
      comCodigoDeSuporte(err, {
        // Âmbar, não o check verde: já realizado é bloqueio, não aprovação.
        icon: 'warning',
        // `...Parcial`: corpo incompleto mostra `--` no campo que faltou em vez
        // de derrubar o cartão inteiro e virar um texto genérico.
        ...corpoJaRealizado(dadosJaRealizadoParcial(err), linha?.janelas ?? []),
      }),
    );
  }

  /**
   * Uma escrita de check-in, um transporte.
   *
   * O fallback legado saiu: desde o contrato 3.8.0 (`guedder-api` `7c61d6630`)
   * o `CheckinPayload` tem `forcar`, e o `CheckInControllerV3` implementa a
   * escrita de verdade — não é mais 501. Então esta rota registra check-in
   * exclusivamente pela v3, forçando ou não, que era o critério da GUE-392 e o
   * que impede as duas cópias da regra de divergirem.
   *
   * Autenticação é o token do usuário, pelo interceptor de sempre: o controller
   * v3 resolve o usuário logado e checa a permissão no banco. O JWT contextual
   * (`aud=checkin-service`) NÃO serve aqui — ver `checkin-v3.ts`.
   */
  private escrever(codigo: string, forcar: boolean): Observable<unknown> {
    return this.v3.registrar(this.idEvento, codigo, forcar);
  }

  /**
   * Erro de escrita de check-in, num só lugar para os três pontos de entrada
   * (linha, câmera e lote).
   *
   * A ordem é a do #116 e continua valendo: quem decide é `code`/`forceAllowed`,
   * nunca o texto de `detail`. O que entrou depois são os ramos que faltavam —
   * 403, indisponibilidade e o 409 que este cliente não sabe ler.
   */
  private tratarErroCheckin(
    err: unknown,
    opts: {
      forcar: boolean;
      linha?: CheckinRowVm;
      /** Repetir a operação forçando — só oferecido quando o contrato permite. */
      forcando: () => void;
      textoGenerico: string;
    },
  ): void {
    // Sem closeLoader em nenhum ramo: o diálogo é a única saída.
    if (ehCheckinJaRealizado(err)) {
      // Não há o que forçar (`forceAllowed: false` no contrato). A lista da tela
      // está desatualizada, então recarrega.
      void this.avisarJaRealizado(err, opts.linha);
      this.list.refresh();
      return;
    }

    // `ehIngressoExpirado` já exige `forceAllowed === true` — a permissão de
    // forçar vem do contrato, não desta tela.
    if (!opts.forcar && ehIngressoExpirado(err)) {
      const d = dadosIngressoExpirado(err);
      const detalhes =
        d.periodo === 'Não informado'
          ? `<p style="text-align:left">${detalheProblema(err, 'Nenhuma janela de check-in aberta agora.')}</p>`
          : `<div class="text-start"><p><strong>Participante:</strong> ${d.nomeParticipante}</p>` +
            `<p><strong>Lote:</strong> ${d.nomeLote}</p>` +
            `<p><strong>Período de Check-in do Lote:</strong></p>` +
            `<p style="margin-left:1rem;color:var(--text-secondary)">${d.periodo}</p></div>`;
      Swal.fire(
        comCodigoDeSuporte(err, {
          icon: 'warning',
          title: 'Fora da janela de check-in',
          html: detalhes,
          showCancelButton: true,
          cancelButtonText: 'Cancelar',
          confirmButtonText: 'Forçar Check-in',
          reverseButtons: true,
        }),
      ).then((r) => {
        if (r.isConfirmed) opts.forcando();
      });
      return;
    }

    if (ehSemPermissao(err)) {
      Swal.fire(
        opcoesErro(err, {
          titulo: 'Sem permissão para registrar check-in',
          texto: detalheProblema(
            err,
            'Sua permissão neste evento não inclui registrar check-in. Fale com quem administra o evento.',
          ),
        }),
      );
      return;
    }

    if (ehIndisponivel(err)) {
      // A tentativa já foi repetida com a MESMA Idempotency-Key antes de chegar
      // aqui, então o servidor pode ter gravado e perdido a resposta. Manda
      // conferir a lista, não tentar de novo às cegas.
      this.list.refresh();
      this.buscarBalanco();
      Swal.fire(
        opcoesErro(err, {
          titulo: 'Sem resposta do servidor',
          texto:
            'Não foi possível confirmar o check-in. Ele pode ter sido registrado — confira na lista antes de tentar de novo.',
        }),
      );
      return;
    }

    // 409 que não casou com nenhum código conhecido, ou força já tentada:
    // diálogo genérico e nenhuma ação de força. Um conflito que este cliente não
    // sabe ler não autoriza nada.
    if ((err as { status?: unknown } | null)?.status === 409) {
      this.list.refresh();
      Swal.fire(
        opcoesErro(err, {
          titulo: 'Check-in não registrado',
          texto: detalheProblema(
            err,
            'Este ingresso já teve check-in, foi cancelado ou está fora da janela.',
          ),
        }),
      );
      return;
    }

    Swal.fire(
      opcoesErro(
        err,
        ehSessaoExpirada(err)
          ? {
              titulo: 'Sessão expirada',
              texto: 'Entre novamente para continuar registrando check-ins.',
            }
          : {
              titulo: 'Check-in não registrado',
              texto: detalheProblema(err, opts.textoGenerico),
            },
      ),
    );
  }

  private registrarCheckin(row: CheckinRowVm, forcar: boolean): void {
    if (row.codigo === '--') return;

    this.emCheckin.update((s) => new Set(s).add(row.id));
    const encerrar = () =>
      this.emCheckin.update((s) => {
        const n = new Set(s);
        n.delete(row.id);
        return n;
      });

    this.escrever(row.codigo, forcar).subscribe({
      next: () => {
        encerrar();
        this.historico.set(
          registrar(
            this.idEvento,
            {
              nome: row.nome,
              horario: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
              lote: row.lote,
              operador: this.operador(),
            },
            this.historico(),
          ),
        );
        this.mostrarToast(row);
        this.list.refresh();
        this.buscarBalanco();
      },
      error: (err: unknown) => {
        encerrar();
        this.tratarErroCheckin(err, {
          forcar,
          linha: row,
          forcando: () => this.registrarCheckin(row, true),
          textoGenerico: 'Não foi possível salvar. O ingresso continua pendente — tente de novo.',
        });
      },
    });
  }

  /** Toast do lote — mesmo canal do individual, sem modal no caminho feliz. */
  private mostrarToastLote(titulo: string, tom: 'sucesso' | 'parcial'): void {
    clearTimeout(this.toastTimer);
    this.toast.set({
      titulo,
      detalhe: tom === 'sucesso' ? 'Check-in em massa' : 'Confira os que falharam na lista',
      podeDesfazer: false,
    });
    this.toastTimer = setTimeout(() => this.toast.set(null), 6000);
  }

  private mostrarToast(row: CheckinRowVm, acao = 'Check-in'): void {
    clearTimeout(this.toastTimer);
    this.toast.set({
      titulo: `${acao} de ${row.nome} registrado`,
      detalhe: [row.lote, row.codigo].filter((v) => v && v !== '--').join(' · '),
      podeDesfazer: this.vmContext().podeDesfazer,
    });
    this.toastTimer = setTimeout(() => this.toast.set(null), 6000);
  }

  fecharToast(): void {
    clearTimeout(this.toastTimer);
    this.toast.set(null);
  }

  onConviteSalvo(): void {
    this.list.refresh();
    this.buscarBalanco();
  }

  /**
   * Etiquetas da seleção inteira, no mesmo modal do individual.
   *
   * Não filtra nada: diferente do check-in em lote, imprimir uma etiqueta não
   * muda estado do ingresso e não tem caso "fora da janela" para decidir a
   * mão — qualquer linha selecionada pode virar papel. A seleção pode estar
   * vazia se a lista paginou desde a marcação; o modal diz isso na cara.
   */
  imprimirEtiquetasEmLote(): void {
    const ids = new Set(this.selecionados().map(String));
    this.etiqueta().abrirLote(this.linhas().filter((l) => ids.has(l.id)).map((l) => l.bruto));
  }

  /**
   * Check-in de vários de uma vez. Pula quem já está presente e quem cairia
   * fora da janela — forçar em massa esconderia justamente o caso que pede
   * decisão humana; esses continuam um a um, pelo botão da linha.
   */
  checkinEmLote(): void {
    const ids = new Set(this.selecionados().map(String));
    const alvos = this.linhas().filter(
      (l) => ids.has(l.id) && l.acoes.includes('checkin') && !l.forcaraCheckin,
    );
    const forcariam = this.linhas().filter(
      (l) => ids.has(l.id) && l.acoes.includes('checkin') && l.forcaraCheckin,
    ).length;

    if (alvos.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nada a registrar',
        text: forcariam
          ? `${forcariam} ingresso(s) selecionado(s) estão fora da janela — registre um a um para confirmar cada caso.`
          : 'Os ingressos selecionados já têm check-in.',
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: `Registrar check-in de ${alvos.length} ingresso(s)?`,
      text: forcariam ? `${forcariam} fora da janela ficam de fora e seguem um a um.` : undefined,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Registrar',
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      // Sem dispararLoader: o overlay cobre a tela inteira e, com o refresh
      // logo depois, produzia o "pisca" — a lista sumia e voltava sem que nada
      // dissesse o que aconteceu. O botão já fica desabilitado por `emLote`.
      this.emLote.set(true);
      // `alvos` já exclui quem cairia fora da janela, então nenhum item do lote
      // é forçado — todos vão pela v3, cada um com sua própria chave de
      // idempotência.
      forkJoin(
        alvos.map((l) =>
          this.escrever(l.codigo, false).pipe(
            map(() => ({ ok: true, linha: l, erro: null as unknown })),
            catchError((erro: unknown) => of({ ok: false, linha: l, erro })),
          ),
        ),
      ).subscribe((resultados) => {
        const ok = resultados.filter((r) => r.ok);
        const falhas = resultados.filter((r) => !r.ok);
        this.emLote.set(false);

        // Cada sucesso entra no histórico, como no check-in individual — senão
        // o card "Último check-in" ignoraria um lote inteiro.
        for (const r of ok) {
          this.historico.set(
            registrar(
              this.idEvento,
              {
                nome: r.linha.nome,
                horario: new Date().toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
                lote: r.linha.lote,
                operador: this.operador(),
              },
              this.historico(),
            ),
          );
        }

        this.list.refresh();
        this.buscarBalanco();

        // Mesmo canal do check-in individual: toast, não modal.
        if (falhas.length === 0) {
          this.mostrarToastLote(`${ok.length} check-in(s) registrado(s)`, 'sucesso');
          return;
        }
        this.mostrarToastLote(
          `${ok.length} de ${resultados.length} registrado(s) — ${falhas.length} falhou(ram)`,
          'parcial',
        );
        // O primeiro erro leva o traceId; os demais costumam ser o mesmo motivo.
        const primeiro = falhas[0].erro;
        if (ehSessaoExpirada(primeiro)) {
          Swal.fire(
            opcoesErro(primeiro, {
              titulo: 'Sessão expirada',
              texto: 'Entre novamente para continuar registrando check-ins.',
            }),
          );
          return;
        }
        if (ehSemPermissao(primeiro)) {
          Swal.fire(
            opcoesErro(primeiro, {
              titulo: 'Sem permissão para registrar check-in',
              texto: detalheProblema(
                primeiro,
                'Sua permissão neste evento não inclui registrar check-in. Fale com quem administra o evento.',
              ),
            }),
          );
        }
      });
    });
  }

  // ── ações do cabeçalho ────────────────────────────────────────────────
  abrirScanner(): void {
    this.scannerAberto.set(true);
  }

  fecharScanner(): void {
    this.scannerAberto.set(false);
    // UMA recarga por sessão de scanner, não uma por leitura. Enquanto a
    // câmera está aberta a lista está coberta por ela — recarregar a cada
    // ingresso era trabalho que ninguém via, e no mobile ainda concorria com o
    // decodificador pela CPU.
    if (this.listaDesatualizada) {
      this.listaDesatualizada = false;
      this.list.refresh();
      this.buscarBalanco();
    }
  }

  /**
   * Check-in vindo da câmera. Atualiza o que a tela mostra SEM ir ao servidor:
   * o histórico local e os dois contadores do topo. A verdade volta do backend
   * quando o scanner fecha.
   */
  onCheckinDoScanner(entrada: CheckinRegistrado): void {
    this.listaDesatualizada = true;
    this.historico.set(registrar(this.idEvento, entrada, this.historico()));
    this.checkinsRealizados.update((n) => n + 1);
    this.checkinsRestantes.update((n) => Math.max(0, n - 1));
  }

  /**
   * Duplo clique na linha faz o check-in, como a dg fazia com a ação padrão.
   *
   * Respeita exatamente a regra do botão: `vm.acoes` já resolveu janela
   * pendente, CASL e preferências. Sem essa guarda o duplo clique viraria um
   * caminho paralelo capaz de disparar o que o botão desabilitado impede.
   */
  checkinPorDuploClique(row: CheckinRowVm): void {
    if (!row.acoes.includes('checkin')) return;
    if (this.emCheckin().has(row.id)) return;
    this.confirmarCheckin(row);
  }

  reenviarIngresso(row: CheckinRowVm): void {
    Swal.fire({
      icon: 'question',
      title: `Reenviar ingresso de ${row.nome}?`,
      text: row.email,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Reenviar',
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      dispararLoader();
      this.http
        .post(adminUrl(replaceEdpointParams(INGRESSOS_REENVIAR, [row.id])), {})
        .subscribe({
          next: () => {
            closeLoader();
            this.mostrarToast(row, 'Reenvio');
          },
          error: (err: unknown) =>
            Swal.fire(
              opcoesErro(err, {
                titulo: 'Ingresso não reenviado',
                texto: 'Não foi possível enviar o e-mail. Tente de novo.',
              }),
            ),
        });
    });
  }

  // ── exportação ────────────────────────────────────────────────────────
  /**
   * Não existe endpoint de exportação de ingressos, então o CSV é montado no
   * cliente. Percorre as páginas do filtro atual até o teto — evento grande
   * merece um endpoint, e aí isto vira uma chamada só.
   *
   * "Do filtro atual" é literal: o arquivo tem que ser o que está na tela.
   * É isso que dispensa agrupar o CSV por janela — quem quer uma janela
   * específica filtra a data e exporta.
   */
  confirmarExportacao(): void {
    Swal.fire({
      title: 'Exportar Check-ins',
      html: TEXTO_CONSENTIMENTO_EXPORTACAO,
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Exportar',
    }).then((r) => {
      if (r.isConfirmed) this.exportar();
    });
  }

  private static readonly TETO_EXPORTACAO = 5000;

  private exportar(): void {
    this.exportando.set(true);
    dispararLoader();
    const total = Math.min(this.list.totalRows(), CheckinPageComponent.TETO_EXPORTACAO);
    const tamanho = 200;
    const paginas = Math.max(1, Math.ceil(total / tamanho));
    const termo = this.list.search().trim();

    // MESMOS filtros da lista, e não só o termo de busca. Antes daqui a
    // exportação mandava apenas `filtro`, ignorando sessão, ordenação e os
    // filtros dinâmicos — inclusive o de DATA. Como o teto de páginas é
    // calculado sobre `totalRows()`, que É filtrado, o arquivo saía com um
    // prefixo NÃO filtrado do mesmo tamanho: parecia certo e estava errado.
    // Com o filtro de data valendo, esta é a resposta para "quem entrou em
    // qual janela" — filtra a data na tela, exporta o que está na tela.
    const filtrosDinamicos = achatarFiltros(this.filtros());
    const requisicoes = Array.from({ length: paginas }, (_, i) => {
      const { tipados, dinamicos } = paramsExportacao({
        page: i,
        size: tamanho,
        termo,
        ordem: this.ordem(),
        sessaoId: this.sessaoId(),
        filtrosDinamicos,
      });
      return buscarIngressosPorEvento(this.http, String(this.idEvento), {
        ...tipados,
        ...dinamicos,
      } as BuscarIngressosPorEventoParams);
    });

    forkJoin(requisicoes).subscribe({
      next: (respostas) => {
        const linhas = respostas
          .flatMap((resp) => parsePagedResponse<Record<string, unknown>>(resp).content)
          .map((cru) => buildCheckinRowVm(cru, this.vmContext()));
        baixarCsv(
          linhasParaCsv(linhas),
          nomeArquivoCsv(String(this.evento()?.['nome'] ?? 'evento'), new Date()),
        );
        closeLoader();
        this.exportando.set(false);
      },
      error: (err: unknown) => {
        this.exportando.set(false);
        Swal.fire(
          opcoesErro(err, {
            titulo: 'Exportação não concluída',
            texto: 'Não foi possível montar o arquivo. Tente de novo.',
          }),
        );
      },
    });
  }
}
